import { Icon } from '../../components/Icon.jsx';
import { getInitials } from '../../lib/identity.js';
import { color, font, radius } from '../../theme/tokens.js';
import { usePlaceDetails } from '../../hooks/usePlaceDetails.js';
import { PhotoCarousel } from './PhotoCarousel.jsx';

/**
 * The reverse face of a swipe card: live Google data without leaving the deck.
 *
 * Shows opening status, an editorial blurb and the two most recent reviews. When
 * Places is not configured this falls back to the bundled description, so the
 * card is never empty.
 *
 * @param {object} props
 * @param {import('../../lib/types.js').Restaurant} props.restaurant
 * @param {boolean} [props.compact]
 * @param {() => void} props.onFlipBack
 */
export function SwipeCardBack({ restaurant, compact = false, onFlipBack }) {
  const { details, loading, photoUrls, photoAttributions } = usePlaceDetails(restaurant);
  const reviews = details?.reviews ?? [];
  const websiteUrl =
    details?.website ??
    restaurant.reservationUrl ??
    restaurant.instagramUrl ??
    `https://www.google.com/search?q=${encodeURIComponent(
      [restaurant.name, restaurant.neighborhood, restaurant.cuisine].filter(Boolean).join(' ')
    )}`;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          position: 'relative',
          height: '40%',
          flexShrink: 0,
          overflow: 'hidden',
          background: color.surfaceSunken,
        }}
      >
        <PhotoCarousel
          restaurant={restaurant}
          photoUrls={photoUrls}
          photoAttributions={photoAttributions}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0) 35%, rgba(0,0,0,0.6) 100%)',
            pointerEvents: 'none',
          }}
        />
        <button
          type="button"
          aria-label="Back to swiping"
          onClick={(event) => {
            event.stopPropagation();
            onFlipBack();
          }}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            width: 28,
            height: 28,
            borderRadius: '50%',
            border: 'none',
            background: 'rgba(0,0,0,0.4)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <Icon name="close" size={14} stroke={color.inkInverse} strokeWidth={2.2} />
        </button>
        <div style={{ position: 'absolute', bottom: 12, left: 12, right: 38 }}>
          <p
            style={{
              margin: '0 0 3px',
              color: color.inkInverse,
              fontSize: 14,
              fontWeight: font.weight.heavy,
              textShadow: '0 1px 6px rgba(0,0,0,0.4)',
            }}
          >
            {restaurant.name}
          </p>
          {details?.rating != null && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                background: 'rgba(0,0,0,0.34)',
                backdropFilter: 'blur(4px)',
                borderRadius: radius.sm,
                padding: '2px 6px',
              }}
            >
              <span style={{ color: color.star, fontSize: 10 }}>★</span>
              <span style={{ color: color.inkInverse, fontSize: 11, fontWeight: font.weight.bold }}>
                {details.rating.toFixed(1)}
              </span>
              <span style={{ color: 'rgba(255,255,255,0.62)', fontSize: 10 }}>
                ({details.reviewCount.toLocaleString()})
              </span>
            </span>
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '9px 13px 4px' }}>
        {details && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 7 }}>
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: details.isOpen ? color.success : color.danger,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: 11,
                fontWeight: font.weight.bold,
                color: details.isOpen ? color.success : color.danger,
              }}
            >
              {details.isOpen == null ? 'Hours unknown' : details.isOpen ? 'Open now' : 'Closed'}
            </span>
            {details.address && (
              <>
                <span style={{ color: color.border, fontSize: 10 }}>·</span>
                <span
                  style={{
                    fontSize: 11,
                    color: color.inkFaint,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {details.address.split(',')[0]}
                </span>
              </>
            )}
          </div>
        )}

        <p
          style={{
            margin: '0 0 8px',
            fontSize: 11,
            lineHeight: 1.55,
            color: color.inkMuted,
            fontStyle: 'italic',
          }}
        >
          {details?.summary ? `"${details.summary}"` : restaurant.description}
        </p>

        {details?.googleSummary && <GoogleSummaryBlock summary={details.googleSummary} />}

        {loading && !details && <SkeletonLines />}

        {/* Google Maps attribution is required when showing Places content without a Google Map. */}
        {details && (
          <a
            href={details.googleMapsUri ?? 'https://maps.google.com'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            style={{
              display: 'inline-block',
              margin: '0 0 8px',
              fontSize: 12,
              color: color.inkFaint,
              textDecoration: 'underline',
            }}
          >
            Powered by Google Maps
          </a>
        )}

        {reviews.length > 0 && (
          <>
            <p
              style={{
                margin: '0 0 6px',
                fontSize: 9,
                fontWeight: font.weight.heavy,
                textTransform: 'uppercase',
                letterSpacing: '1.2px',
                color: color.inkFaint,
              }}
            >
              What people say
            </p>
            {reviews.slice(0, 2).map((review, position) => (
              <div
                key={`${review.author}-${review.time}`}
                style={{
                  marginBottom: 7,
                  paddingBottom: 7,
                  borderBottom: position === 0 ? `1px solid ${color.border}` : 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                  <Avatar name={review.author} />
                  {/* Google Maps reviews link back to the source review when Google provides it. */}
                  {review.googleMapsUri || review.authorUri ? (
                    <a
                      href={review.googleMapsUri ?? review.authorUri}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => event.stopPropagation()}
                      style={{
                        fontSize: 11,
                        fontWeight: font.weight.semibold,
                        color: color.ink,
                        textDecoration: 'underline',
                      }}
                    >
                      {review.author}
                    </a>
                  ) : (
                    <span
                      style={{ fontSize: 11, fontWeight: font.weight.semibold, color: color.ink }}
                    >
                      {review.author}
                    </span>
                  )}
                  <span style={{ fontSize: 9, color: color.inkFaint }}>
                    {review.time ? `${review.time} · Google Maps` : 'Google Maps'}
                  </span>
                  <span style={{ color: color.star, fontSize: 10, marginLeft: 'auto' }}>
                    {'★'.repeat(Math.round(review.rating))}
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: 11, lineHeight: 1.5, color: color.inkMuted }}>
                  {review.text}
                </p>
              </div>
            ))}
          </>
        )}
      </div>

      <div
        style={{
          padding: '7px 12px 12px',
          flexShrink: 0,
          borderTop: `1px solid ${color.border}`,
        }}
      >
        {compact ? (
          <div style={splitActionRowStyle}>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onFlipBack();
              }}
              style={splitSecondaryButtonStyle}
            >
              Back to swiping
            </button>
            <a
              href={websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
              style={splitPrimaryLinkStyle}
            >
              Website ↗
            </a>
          </div>
        ) : (
          <a
            href={websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            style={primaryLinkStyle}
          >
            Visit Website ↗
          </a>
        )}
      </div>
    </div>
  );
}

function GoogleSummaryBlock({ summary }) {
  return (
    <div
      style={{
        margin: '0 0 10px',
        padding: 9,
        borderRadius: radius.sm,
        background: color.surfaceSunken,
      }}
    >
      <p style={{ margin: '0 0 4px', fontSize: 11, lineHeight: 1.5, color: color.inkMuted }}>
        {summary.text}
      </p>
      <p style={{ margin: '0 0 5px', fontSize: 9.5, color: color.inkFaint }}>
        {summary.disclosure}
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {summary.googleMapsUri && (
          <a
            href={summary.googleMapsUri}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            style={{ fontSize: 10, color: color.primary }}
          >
            View on Google Maps
          </a>
        )}
        {summary.flagUri && (
          <a
            href={summary.flagUri}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            style={{ fontSize: 10, color: color.primary }}
          >
            Report this summary
          </a>
        )}
      </div>
    </div>
  );
}

const splitActionRowStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 8,
};

const primaryLinkStyle = {
  display: 'block',
  width: '100%',
  padding: 10,
  borderRadius: radius.md,
  background: color.primary,
  border: 'none',
  color: color.inkInverse,
  fontSize: 13,
  fontWeight: font.weight.bold,
  textAlign: 'center',
  textDecoration: 'none',
  cursor: 'pointer',
};

const splitButtonBaseStyle = {
  width: '100%',
  minHeight: 38,
  padding: '9px 10px',
  borderRadius: radius.md,
  fontSize: 12,
  fontWeight: font.weight.bold,
  textAlign: 'center',
  textDecoration: 'none',
  cursor: 'pointer',
};

const splitSecondaryButtonStyle = {
  ...splitButtonBaseStyle,
  background: color.surface,
  border: `1.5px solid ${color.border}`,
  color: color.inkMuted,
};

const splitPrimaryLinkStyle = {
  ...splitButtonBaseStyle,
  display: 'block',
  background: color.primary,
  border: `1.5px solid ${color.primary}`,
  color: color.inkInverse,
};

function Avatar({ name }) {
  return (
    <div
      style={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        background: color.ink,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <span style={{ color: color.inkInverse, fontSize: 9, fontWeight: font.weight.bold }}>
        {getInitials(name)[0]}
      </span>
    </div>
  );
}

function SkeletonLines() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {[0, 1].map((line) => (
        <div key={line} style={{ height: 44, background: color.surfaceSunken, borderRadius: 8 }} />
      ))}
    </div>
  );
}
